/**
 * HelpMenu
 *
 * A dropdown menu triggered by a HelpCircle icon button.
 * Shows tours relevant to the current page and includes Documentation
 * and Contact Support links.
 *
 * Keyboard shortcut: pressing "?" (when not focused on an input) opens the menu.
 */

import { useEffect, useRef, useState } from 'react';
import {
  HelpCircle,
  Play,
  CheckCircle2,
  RotateCcw,
  Compass,
  BookOpen,
  MessageCircle,
  Clock,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTour } from '@/contexts/TourContext';
import { getToursByPage } from '@/config/tours';
import type { Tour } from '@/types/product-tour';

interface HelpMenuProps {
  currentPath: string;
  onNavigate?: (path: string) => void;
}

export function HelpMenu({ currentPath, onNavigate }: HelpMenuProps) {
  const { startTour, restartTour, getTourState, isActive } = useTour();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const pageTours = getToursByPage(currentPath);

  // ── "?" keyboard shortcut ─────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '?') return;
      const tag = (e.target as HTMLElement).tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if ((e.target as HTMLElement).isContentEditable) return;
      e.preventDefault();
      setOpen(prev => !prev);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleTourClick = (tour: Tour) => {
    setOpen(false);
    const state = getTourState(tour.id);
    if (state?.status === 'completed') {
      restartTour(tour.id);
    } else {
      startTour(tour.id);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.max(1, Math.round(seconds / 60));
    return `~${mins} min`;
  };

  return (
    <TooltipProvider>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                ref={triggerRef}
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="Open tours menu"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Tours &amp; Help <kbd className="ml-1 rounded bg-muted px-1 font-mono text-[10px]">?</kbd>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent align="end" className="w-72">
          {/* Tours section */}
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wide pb-1">
            Page Tours
          </DropdownMenuLabel>

          {pageTours.length === 0 ? (
            <div className="flex flex-col items-center gap-1.5 py-4 text-center">
              <Compass className="h-6 w-6 text-muted-foreground/40" />
              <p className="text-xs text-muted-foreground">No tours available for this page yet.</p>
            </div>
          ) : (
            pageTours.map(tour => {
              const state = getTourState(tour.id);
              const isCompleted  = state?.status === 'completed';
              const isInProgress = state?.status === 'in_progress';
              const isDisabled   = isActive;

              return (
                <DropdownMenuItem
                  key={tour.id}
                  className="flex flex-col items-start gap-1 py-2.5 cursor-pointer"
                  disabled={isDisabled}
                  onClick={() => handleTourClick(tour)}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {isCompleted ? (
                        <RotateCcw className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      ) : (
                        <Play className="h-3.5 w-3.5 shrink-0 text-primary fill-primary/20" />
                      )}
                      <span className="text-sm font-medium">{tour.name}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isCompleted && (
                        <Badge variant="secondary" className="text-[10px] h-4 px-1 gap-0.5 font-normal">
                          <CheckCircle2 className="h-2.5 w-2.5 text-teal-500" />
                          Done
                        </Badge>
                      )}
                      {isInProgress && (
                        <Badge variant="outline" className="text-[10px] h-4 px-1 font-normal text-warning-600 border-warning-300">
                          In Progress
                        </Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1 pl-5">
                    {tour.description}
                  </p>
                  {tour.estimatedDuration && (
                    <div className="flex items-center gap-1 pl-5 text-[10px] text-muted-foreground/70">
                      <Clock className="h-2.5 w-2.5" />
                      {formatDuration(tour.estimatedDuration)}
                    </div>
                  )}
                </DropdownMenuItem>
              );
            })
          )}

          <DropdownMenuSeparator />

          {/* Documentation */}
          <DropdownMenuItem
            className="gap-2 cursor-pointer"
            onClick={() => onNavigate?.('/help')}
          >
            <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm">Documentation</span>
          </DropdownMenuItem>

          {/* Contact Support */}
          <DropdownMenuItem
            className="gap-2 cursor-pointer"
            onClick={() => onNavigate?.('/support')}
          >
            <MessageCircle className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm">Contact Support</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <div className="px-2 pb-1.5 pt-1">
            <p className="text-[10px] text-muted-foreground/60">
              Press <kbd className="rounded bg-muted px-1 font-mono text-[10px]">?</kbd> to open this menu
            </p>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
